import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Chip, TextField, Button, CircularProgress, Checkbox } from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Cancel as CancelIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import BookPage from './BookPage';
import RecipeScaler from './RecipeScaler';
import { Recipe } from '../models/Recipe';

interface RecipeLeftPageProps {
  recipe: Recipe;
  currentScale: number;
  onBack: () => void;
  onScaleChange: (scaledRecipe: Recipe, scale: number) => void;
  onIngredientsChange?: (updatedIngredients: string[]) => void;
  onStartCooking?: () => void;
  generatingSteps?: boolean;
  metaChips?: Array<{ icon?: any; label: string }>;
  pageNumber?: number;
  ingredientCheckedStatus?: Record<string, boolean>;
  onIngredientCheckedChange?: (ingredient: string, checked: boolean) => void;
}

const RecipeLeftPage: React.FC<RecipeLeftPageProps> = ({
  recipe,
  currentScale,
  onBack,
  onScaleChange,
  onIngredientsChange,
  onStartCooking,
  generatingSteps = false,
  metaChips = [],
  pageNumber,
  ingredientCheckedStatus = {},
  onIngredientCheckedChange,
}) => {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedIngredient, setEditedIngredient] = useState<string>('');
  const [modifiedIngredients, setModifiedIngredients] = useState<string[]>([]);
  const [hasModifications, setHasModifications] = useState<boolean>(false);

  // Reset local modifications when recipe changes (e.g., from scaling)
  useEffect(() => {
    setHasModifications(false);
    setModifiedIngredients([]);
    setEditingIndex(null);
  }, [recipe.id, recipe.ingredients, currentScale]);

  const ingredientsList = recipe.ingredients
    .split('\n')
    .map((ingredient: string) => ingredient.trim())
    .filter((ingredient: string) => ingredient);

  // Use modified ingredients if available, otherwise use original
  const displayIngredients = hasModifications ? modifiedIngredients : ingredientsList;

  const handleSaveIngredient = () => {
    if (editingIndex !== null) {
      const updatedIngredients = [...displayIngredients];
      updatedIngredients[editingIndex] = editedIngredient.trim();
      setModifiedIngredients(updatedIngredients);
      setHasModifications(true);
      setEditingIndex(null);
      setEditedIngredient('');
      
      // Notify parent component of the changes
      if (onIngredientsChange) {
        onIngredientsChange(updatedIngredients);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedIngredient('');
  };

  return (
    <BookPage isLeftPage={true} pageNumber={pageNumber || (recipe.id ? parseInt(recipe.id.slice(-2), 16) % 100 + 10 : 10)}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <Button 
            onClick={onBack} 
            startIcon={<ArrowBackIcon />}
            sx={{ 
              color: 'text.primary',
              fontFamily: 'inherit',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: 'rgba(139, 69, 19, 0.1)',
              },
            }}
          >
            Back to Recipes
          </Button>
        </Box>

        {/* Recipe Image */}
        {recipe.image && (
          <Box sx={{
            mb: { xs: 2, md: 3 },
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0 4px 12px rgba(139, 69, 19, 0.15)',
          }}>
            <img
              src={recipe.image}
              alt={recipe.title}
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '180px',
                objectFit: 'cover',
              }}
            />
          </Box>
        )}

        {/* Title and Meta Info */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 700,
              color: '#5D4037',
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            }}
          >
            {recipe.title}
            {currentScale !== 1 && (
              <Chip
                label={`${currentScale}x scaled`}
                size="small"
                color="primary"
                sx={{ ml: { xs: 1, md: 2 } }}
              />
            )}
          </Typography>

          {/* Meta chips */}
          {metaChips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {metaChips.map((chip, index) => (
                <Chip
                  key={index}
                  label={chip.label}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(139, 69, 19, 0.1)',
                    color: '#5D4037',
                    fontFamily: '"Crimson Text", serif',
                    fontSize: '0.85rem',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Start Cooking Button */}
          {onStartCooking && (
            <Button
              variant="contained"
              fullWidth
              onClick={onStartCooking}
              disabled={generatingSteps}
              startIcon={generatingSteps ? <CircularProgress size={20} /> : <PlayIcon />}
              sx={{
                backgroundColor: '#FF6B35',
                color: 'white',
                fontFamily: '"Crimson Text", serif',
                fontSize: { xs: '1rem', md: '1.1rem' },
                py: { xs: 1.25, md: 1.5 },
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
                '&:hover': {
                  backgroundColor: '#E55A2B',
                  boxShadow: '0 6px 16px rgba(255, 107, 53, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(255, 107, 53, 0.5)',
                },
              }}
            >
              {generatingSteps ? 'Generating Steps...' : 'Start Cooking'}
            </Button>
          )}
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          style={{ flex: 1 } as any}
        >
          {/* Recipe Scaler */}
          <div id="scaler-section">
            <RecipeScaler 
              recipe={recipe}
              onScaleChange={onScaleChange}
            />
          </div>

          {/* Ingredients Section */}
          <Box>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontFamily: '"Crimson Text", serif',
                fontWeight: 600,
                color: '#6D4C41',
                borderBottom: '2px solid rgba(139, 69, 19, 0.2)',
                pb: 1,
                mb: 2,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
              Ingredients
              {currentScale !== 1 && (
                <Typography
                  component="span"
                  variant="body1"
                  sx={{
                    ml: 1,
                    fontWeight: 600,
                    color: 'primary.main',
                    fontStyle: 'italic',
                  }}
                >
                  (scaled to {currentScale}x)
                </Typography>
              )}
            </Typography>
            <Box sx={{ pl: 1 }}>
              {displayIngredients.map((ingredient: string, index: number) => (
                <Box 
                  key={index}
                  sx={{ 
                    display: 'flex',
                    alignItems: 'flex-start',
                    mb: 1.5, // Increased spacing
                    py: 0.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '1.2rem',
                      color: 'text.secondary',
                      mr: 1.5,
                      lineHeight: 1.5,
                    }}
                  >
                    •
                  </Typography>
                  {editingIndex === index ? (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        value={editedIngredient}
                        onChange={(e) => setEditedIngredient(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ 
                          flex: 1,
                          '& .MuiOutlinedInput-root': {
                            fontFamily: '"Merriweather", serif',
                            fontSize: '14px',
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveIngredient();
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        autoFocus
                      />
                      <IconButton
                        size="small"
                        onClick={handleSaveIngredient}
                        sx={{ color: 'primary.main' }}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelEdit}
                        sx={{ color: 'text.secondary' }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography
                        variant="body1" // Uses theme body1 (1.125rem)
                        sx={{
                          color: hasModifications ? 'primary.main' : (currentScale !== 1 ? 'primary.main' : 'text.primary'),
                          fontWeight: hasModifications || currentScale !== 1 ? 600 : 400,
                        }}
                      >
                        {ingredient}
                      </Typography>
                      <Checkbox
                        checked={ingredientCheckedStatus[ingredient] ?? false}
                        onChange={(e) => {
                          if (onIngredientCheckedChange) {
                            onIngredientCheckedChange(ingredient, e.target.checked);
                          }
                        }}
                        sx={{
                          color: 'rgba(139, 69, 19, 0.6)',
                          '&.Mui-checked': {
                            color: '#FF6B35',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(139, 69, 19, 0.1)',
                          },
                        }}
                      />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </motion.div>
      </Box>
    </BookPage>
  );
};

export default RecipeLeftPage;
